/**
 * Help & Guide — always-available in-app onboarding: a live setup checklist plus
 * a step-by-step walkthrough (sign-in, 2FA, Alpaca, bot preferences, go live) and FAQ.
 */
import React from 'react';
import { useAuth } from '../context/AuthContext';
import { OnboardingChecklist, replayWelcome } from '../components/Onboarding';
import {
  Sparkles, ShieldCheck, Link2, Sliders, Play, Eye, BookOpen,
  Ticket, KeyRound, Bot, TrendingUp, LifeBuoy, RotateCcw,
} from 'lucide-react';

const STEPS = [
  {
    n: '01', icon: Ticket, title: 'Sign in with your invite',
    body: 'Paeru is invite-only. Open the link you were sent, pick a username, and choose a strong password (10+ characters — the strength bar must reach green; common or breached passwords are rejected). A short phrase of a few random words works great.',
  },
  {
    n: '02', icon: ShieldCheck, title: 'Turn on two-factor authentication',
    body: 'Required, and quick. Install an authenticator app (Google Authenticator, Authy, or 1Password), scan the QR code, and enter the 6-digit code. Save your 10 backup codes — each works once if you lose your phone. From then on, signing in is username → password → 6-digit code.',
  },
  {
    n: '03', icon: Link2, title: 'Connect a free Alpaca paper account',
    body: 'Paeru trades simulated money through Alpaca. Create a free account at alpaca.markets, open the Paper Trading dashboard, and generate an API key (Key ID + Secret). In Settings → Alpaca Integration → Connect Alpaca, paste both and verify. The top bar then shows a green "Alpaca Connected" pill.',
  },
  {
    n: '04', icon: Sliders, title: 'Set your bot preferences',
    body: 'In Settings → Bot Trading Settings, set the three core dials: Maximum Concurrent Positions (how many stocks at once), Max Daily Trades (a hard cap), and Min AI Score (the quality bar — higher trades less but pickier). The Advanced Bot Settings button opens the full controls.',
  },
  {
    n: '05', icon: Bot, title: 'Choose a strategy & guardrails',
    body: 'On Bot Control, pick one or more strategies — 🌊 Swing (2–10 day holds) is the friendliest start, plus ⚡ Day and 🏔️ Long. Set risk % per trade and your stop-loss / take-profit (defaults are fine; the bot also uses volatility-based ATR stops). Leave trading days and market-hours-only on for normal weekday trading.',
  },
  {
    n: '06', icon: Play, title: 'Start the bot & watch it work',
    body: 'On Bot Control, press Start Bot — it now scans, thinks, and trades your paper account on its own. Watch the War Room (the AI analysts reaching a verdict), Positions and Dashboard (live holdings), and Performance (fills in once trades close). If it doesn\'t trade right away, that\'s by design — "no trade" is a real decision. Press Stop Bot anytime.',
  },
];

const STARTER = [
  ['Strategy', '🌊 Swing'],
  ['Max concurrent positions', '5'],
  ['Max daily trades', '5–10'],
  ['Min AI score', '55'],
  ['Risk per trade', '2%'],
  ['Stop-loss / take-profit', 'Defaults (ATR)'],
];

const FAQ = [
  { q: "It's paper, always", a: 'Every trade is simulated. Nothing here touches real money, so experiment freely.' },
  { q: 'Lost your phone?', a: 'Sign in with a backup code, then re-add 2FA in Settings. Out of codes? Ask an admin to reset it.' },
  { q: "You're in control", a: 'The bot only ever touches your connected account, and Stop Bot halts it instantly.' },
  { q: 'Where to watch', a: 'War Room shows the AI debate; Positions and Dashboard show live holdings; Performance fills in as trades close.' },
];

export default function Help() {
  const { user } = useAuth();

  return (
    <div className="max-w-3xl mx-auto px-1 py-2">
      {/* Hero */}
      <div className="flex items-start gap-4 mb-8">
        <div className="w-12 h-12 rounded-2xl grid place-items-center flex-none" style={{ background: 'linear-gradient(140deg,#E7B24C,#C0902E)' }}>
          <LifeBuoy className="w-6 h-6" style={{ color: '#2b1c04' }} />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Help &amp; Guide</h1>
          <p className="text-text-secondary mt-1">Everything you need to get from sign-in to your first bot trade. It's all paper money — zero real risk.</p>
        </div>
      </div>

      {/* Live checklist */}
      <div className="card p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5 text-accent-yellow" /> Your setup checklist</h2>
          <button onClick={() => { replayWelcome(user?.id); window.location.reload(); }} className="text-xs text-text-muted hover:text-text-primary flex items-center gap-1" title="Show the welcome pop-up again">
            <RotateCcw className="w-3.5 h-3.5" /> Replay welcome
          </button>
        </div>
        <OnboardingChecklist />
      </div>

      {/* Steps */}
      <h2 className="font-semibold text-lg mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5 text-accent-blue" /> Step-by-step</h2>
      <div className="flex flex-col gap-3 mb-8">
        {STEPS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.n} className="card p-5 flex gap-4">
              <div className="flex-none w-10 h-10 rounded-xl grid place-items-center font-mono font-semibold text-sm border border-accent-yellow/30 text-accent-yellow bg-accent-yellow/5">{s.n}</div>
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-1"><Icon className="w-4 h-4 text-text-muted" /> {s.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{s.body}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Starter settings */}
      <div className="card overflow-hidden mb-8">
        <div className="p-4 border-b border-border-color flex items-center justify-between bg-bg-tertiary/40">
          <h2 className="font-semibold flex items-center gap-2"><TrendingUp className="w-5 h-5 text-accent-green" /> Recommended starter settings</h2>
          <span className="text-xs font-mono uppercase tracking-wide text-text-muted">paper · low-stress</span>
        </div>
        <div className="divide-y divide-border-color">
          {STARTER.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-5 py-3">
              <span className="text-sm text-text-muted">{k}</span>
              <span className="text-sm font-semibold font-mono">{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <h2 className="font-semibold text-lg mb-4 flex items-center gap-2"><KeyRound className="w-5 h-5 text-accent-purple" /> Good to know</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {FAQ.map((f) => (
          <div key={f.q} className="card p-4">
            <h4 className="font-medium text-sm mb-1 flex items-center gap-2"><Eye className="w-3.5 h-3.5 text-accent-yellow" /> {f.q}</h4>
            <p className="text-xs text-text-muted leading-relaxed">{f.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
